const { fetchJSON, serialiseForm, getElement, getElements } = window.BAO.Utils

/**
 * @param {EmailCheckService.Request} body
 */
async function verifyEmailExists (body) {
  const url = `${window.theme.appSettings.baseUrl}/api/email-check`

  try {
    /** @type {EmailCheckService.Response} */
    const response = await fetchJSON(url, {
      method: 'POST',
      body: JSON.stringify(body),
    })

    return response?.success
  } catch (error) {
    console.error(error)

    return false
  }
}

export class TradeForm extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'actions',
      'login-form-container',
      'register-form-container',
      'register-form',
      'fauxemail',
      'fauxemail-form',
      'fauxemail-form-submit-row',
      'email',
      'footer',
      'restart-trigger',
      'ometria-form',
    ]
  }

  setupListeners () {
    super.setupListeners()

    if (!this.els['proof-input']?.exists) return

    this.handleProofChange = this.handleProofChange.bind(this)

    this.listeners.add(
      this.els['proof-select'].element,
      'change',
      this.handleProofChange.bind(this),
    )

    this.listeners.add(
      this.els['fauxemail-form'].element,
      'submit',
      this.handleFauxEmailFormSubmit.bind(this),
    )

    this.listeners.add(
      this.els.firstName.element,
      'change',
      this.handleFirstNameChange.bind(this),
    )

    this.listeners.add(
      this.els.lastName.element,
      'change',
      this.handleLastNameChange.bind(this),
    )

    this.listeners.add(
      this.els.fauxemail.element,
      'change',
      this.handleFauxEmailChange.bind(this),
    )

    this.listeners.add(
      this.els['restart-trigger'].element,
      'click',
      this.handleRestartTrigger.bind(this),
    )

    this.listeners.add(
      this.els.nextStep.element,
      'click',
      this.handleNextStepTradeForm.bind(this),
    )

    this.listeners.add(
      this.els.textarea.element,
      'keyup',
      this.handleTextareaChange.bind(this),
    )

    this.listeners.add(
      this.els.prevStep.element,
      'click',
      this.handlePrevStepTradeForm.bind(this),
    )

    this.listeners.add(
      this.els.firstStep.element,
      'click',
      this.handleFirstStepTradeForm.bind(this),
    )

    // Set up registration form submission handling for Ometria
    if (this.els['register-form']?.exists) {
      this.listeners.add(
        this.els['register-form'].element,
        'submit',
        this.handleRegistrationFormSubmit.bind(this),
      )
    }

    if (window.location.href.indexOf('contact_posted=true') > -1) {
      this.toggleElement(this.els['trade-form-container'].element, true)

      this.toggleElement(
        this.els['trade-form-container-submitted'].element,
        false,
      )

      localStorage.setItem('userChangingEmail', 'false')
    } else if (localStorage.getItem('userChangingEmail') === 'true') {
      document.querySelector('.sec-TradeForm_Trigger').click()
    }

    // Auto-open modal and show trade form if customer just logged in/registered
    this.checkForCustomerLogin()
  }

  get stepContainerOneEl () {
    return this.els?.['step-container-one']?.element
  }

  get stepContainerTwoEl () {
    return this.els?.['step-container-two']?.element
  }

  get requiredEls () {
    return getElements('[required], [aria-required="true"]', { context: this })
  }

  checkForCustomerLogin () {
    const isCustomerLoggedIn = document.body.classList.contains(
      'lyt-Site-customerSignedIn',
    )

    // Check if we should auto-open the trade form (e.g., after login/register)
    const shouldAutoOpen =
      localStorage.getItem('openTradeFormAfterLogin') === 'true' ||
      (window.location.href.includes('return_to') &&
        window.location.href.includes('trade-program'))

    const isContactPosted =
      window.location.href.includes('contact_posted=true') &&
      window.location.hash.includes('trade_form')

    if (isCustomerLoggedIn && shouldAutoOpen && !isContactPosted) {
      setTimeout(() => {
        document.querySelector('.sec-TradeForm_Trigger')?.click()

        // Show the trade form container and hide login/register forms
        this.toggleElement(this.els['login-form-container'].element, true)
        this.toggleElement(this.els['register-form-container'].element, true)
        this.toggleElement(this.els['trade-form-container'].element, false)
        this.toggleActions(true)
        this.toggleFooter(true)

        // Submit stored Ometria data after successful registration
        this.submitStoredOmetriaData()

        // Only clear the flag after modal has been open for 10 seconds
        setTimeout(() => {
          localStorage.removeItem('openTradeFormAfterLogin')
        }, 10000)
      }, 100)
    }

    if (isContactPosted) {
      setTimeout(() => {
        document.querySelector('.sec-TradeForm_Trigger')?.click()
      }, 100)
    }
  }

  /**
   * @param {SubmitEvent} event
   * Store registration data for Ometria submission after page refresh
   */
  handleRegistrationFormSubmit (event) {
    // Set flag for trade form auto-open
    localStorage.setItem('openTradeFormAfterLogin', 'true')

    const registerForm = event.currentTarget
    const emailField = registerForm.querySelector('[name="customer[email]"]')
    const firstNameField = registerForm.querySelector(
      '[name="customer[first_name]"]',
    )
    const lastNameField = registerForm.querySelector(
      '[name="customer[last_name]"]',
    )
    const acceptsMarketingCheckbox = registerForm.querySelector(
      '#trade_accepts_marketing',
    )

    // Store customer data for Ometria submission after page refresh
    if (emailField) {
      const customerData = {
        email: emailField.value,
        firstName: firstNameField?.value || '',
        lastName: lastNameField?.value || '',
        acceptsMarketing:
          acceptsMarketingCheckbox && acceptsMarketingCheckbox.checked,
      }

      localStorage.setItem('tradeFormOmetriaData', JSON.stringify(customerData))
    }
  }

  /**
   * Submit stored Ometria data after customer returns from registration
   */
  submitStoredOmetriaData () {
    const storedData = localStorage.getItem('tradeFormOmetriaData')

    if (!storedData || !this.els['ometria-form']?.exists) {
      return
    }

    try {
      const customerData = JSON.parse(storedData)
      const ometriaForm = this.els['ometria-form'].element

      // Populate Ometria form with stored customer data
      const ometriaEmailFields = ometriaForm.querySelectorAll('[name="ue"]')
      ometriaEmailFields.forEach(field => {
        field.value = customerData.email
      })

      if (customerData.firstName) {
        const firstNameField = ometriaForm.querySelector('[name="first_name"]')
        if (firstNameField) firstNameField.value = customerData.firstName
      }

      if (customerData.lastName) {
        const lastNameField = ometriaForm.querySelector('[name="last_name"]')
        if (lastNameField) lastNameField.value = customerData.lastName
      }

      // Set marketing subscription status in Ometria form
      const ometriaSubscriptionField = ometriaForm.querySelector(
        '[name="@subscription_status"]',
      )
      if (ometriaSubscriptionField) {
        ometriaSubscriptionField.value = customerData.acceptsMarketing
          ? 'SUBSCRIBED'
          : 'UNSUBSCRIBED'
      }

      // Submit to Ometria
      setTimeout(() => {
        const submitEvent = new Event('submit', {
          bubbles: true,
          cancelable: true,
        })
        ometriaForm.dispatchEvent(submitEvent)

        // Clear stored data after successful submission
        localStorage.removeItem('tradeFormOmetriaData')
      }, 200)
    } catch (error) {
      // Clear invalid stored data
      localStorage.removeItem('tradeFormOmetriaData')
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleFauxEmailFormSubmit (event) {
    event.preventDefault()

    /** @type {EmailCheckService.Request} */
    const body = serialiseForm(event.currentTarget)

    if (body.email === '') {
      return
    }

    body.shop = 'abask-com.myshopify.com'

    body.email = body.email.toLowerCase()

    const emailExists = await verifyEmailExists(body)

    this.toggleElement(this.els.actions.element, true)

    if (emailExists === true) {
      this.toggleElement(this.els['login-form-container'].element, false)
    } else {
      this.toggleElement(this.els['register-form-container'].element, false)
    }

    this.toggleFooter(false)
  }

  async handleNextStepTradeForm () {
    const step = {
      isValid: true,
      inputEls: [
        this.els['business-name-input'].element,
        this.els['business-website-input'].element,
        this.els['business-address-line-one-input'].element,
        this.els['business-city-input'].element,
        this.els['business-postcode-input'].element,
        this.els['business-country-input'].element,
        this.els['business-phone-input'].element,
        this.els['proof-select'].element,
        this.els['proof-input'].element,
      ],
    }

    step.inputEls.forEach(element => {
      const isValid = element.checkValidity()
      const messageEl = getElement(this.els.message.selector, {
        context: element.closest(this.els.field.selector),
      })

      element.setAttribute('aria-invalid', !isValid)

      if (!isValid) {
        step.isValid = false
      }

      if (messageEl) {
        messageEl.setAttribute('aria-hidden', isValid)
      }
    })

    if (step.isValid === true) {
      this.toggleElement(this.els['trade-form-intro-one'].element, true)
      this.toggleElement(this.els['trade-form-intro-two'].element, false)
      this.toggleElement(this.els.firstStep.element, true)
      this.toggleElement(this.els.stepOne.element, true)
      this.toggleElement(this.els.stepTwo.element, false)
      this.toggleElement(this.els.nextStep.element, true)
      this.toggleElement(this.els.prevStep.element, false)
      this.toggleElement(this.els.submit.element, false)
      this.toggleElement(this.els['contact-us'].element, false)
      this.toggleActions(true)
      this.toggleFooter(true)
      document.getElementById('trade-form-header').scrollIntoView()
    }
  }

  /**
   * @param {Event} event Input event triggered on input of the fauxemail element
   */
  handleFauxEmailChange (event) {
    /* Update the value of the email elements */
    this.els.email.elements.forEach(
      emailEl => (emailEl.value = event.target.value),
    )
  }

  /**
   * @param {Event} event Input event triggered on input of the first name element
   */
  handleFirstNameChange (event) {
    /* Update the value of the first name elements */
    this.els.firstName.elements.forEach(
      firstNameEl => (firstNameEl.value = event.target.value),
    )
  }

  /**
   * @param {Event} event Input event triggered on input of the last name element
   */
  handleLastNameChange (event) {
    /* Update the value of the last name elements */
    this.els.lastName.elements.forEach(
      lastNameEl => (lastNameEl.value = event.target.value),
    )
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleRestartTrigger (event) {
    event.preventDefault()

    this.toggleElement(this.els['login-form-container'].element, true)
    this.toggleElement(this.els['register-form-container'].element, true)
    this.toggleElement(this.els['trade-form-container'].element, true)
    this.toggleActions(false)
    this.toggleFooter(true)
  }

  handleProofChange () {
    if (this.els['proof-select']?.exists) {
      this.els['proof-field'].element.setAttribute(
        'aria-hidden',
        this.els['proof-select'].element.selectedIndex === 0,
      )
    }

    if (this.els['proof-label']?.exists) {
      this.els['proof-label'].element.innerText = this.els[
        'proof-select'
      ].element.value
    }

    if (this.els['proof-input']?.exists) {
      this.els['proof-input'].element.placeholder = this.els[
        'proof-select'
      ].element.value
    }
  }

  handleTextareaChange () {
    const len = this.els.textarea.element.value.length
    const maxchar = 500
    if (len > maxchar) {
      return false
    } else if (len > 0) {
      this.els['remaining-chars'].element.innerHTML = `${maxchar -
        len} characters remaining`
    }
  }

  handlePrevStepTradeForm (event) {
    event.preventDefault()
    this.toggleElement(this.els['trade-form-intro-one'].element, false)
    this.toggleElement(this.els['trade-form-intro-two'].element, true)
    this.toggleElement(this.els.firstStep.element, false)
    this.toggleElement(this.els.stepOne.element, false)
    this.toggleElement(this.els.stepTwo.element, true)
    this.toggleElement(this.els.nextStep.element, false)
    this.toggleElement(this.els.prevStep.element, true)
    this.toggleElement(this.els.submit.element, true)
    this.toggleElement(this.els['contact-us'].element, true)
    this.toggleActions(true)
    this.toggleFooter(true)
  }

  handleFirstStepTradeForm () {
    localStorage.setItem('userChangingEmail', 'true')
  }

  /**
   * @param {HTMLElement} element The HTMLElement we wish to show/hide
   * @param {boolean} isHidden Whether or not the provided element should be hidden
   */
  toggleElement (element, isHidden) {
    element?.setAttribute('aria-hidden', String(isHidden))
  }

  /**
   * @param {boolean} isHidden Whether or not the actions should be hidden
   */
  toggleActions (isHidden) {
    this.toggleElement(this.els.actions.element, isHidden)
  }

  /**
   * @param {boolean} isHidden Whether or not the footer should be hidden
   */
  toggleFooter (isHidden) {
    this.toggleElement(this.els.footer.element, isHidden)
  }
}

if (!customElements.get('trade-form')) {
  customElements.define('trade-form', TradeForm)
}
